import { useMemo, useState } from 'react';
import {
  Field,
  makeStyles,
  Overflow,
  Tag,
  TagPicker,
  TagPickerControl,
  TagPickerGroup,
  TagPickerInput,
  TagPickerList,
  TagPickerOption,
  TagPickerProps,
  TooltipProps,
  tokens,
  useTagPickerFilter,
} from '@fluentui/react-components';

import { ControlledTooltip } from './ControlledTooltip';
import { MultiselectSummaryItems } from './MultiselectSummary';

const NO_MATCHES_OPTION_VALUE = '__no_matches__';

const useStyles = makeStyles({
  // Fluent's TagPickerOption has no disabled prop, so selection is blocked in onOptionSelect.
  disabledOption: {
    color: tokens.colorNeutralForegroundDisabled,
    cursor: 'not-allowed',
    '&:hover, &:focus, &[aria-selected]': {
      color: tokens.colorNeutralForegroundDisabled,
      backgroundColor: 'transparent',
    },
  },
});

export type TMultiSelectTagPickerOption = {
  value: string;
  label: string;
  searchText?: string;
  disabledReason?: TooltipProps['content'];
};

type TProps = {
  options: TMultiSelectTagPickerOption[];
  selectedOptions: string[];
  onChange: (selectedOptions: string[]) => void;
  inputAriaLabel: string;
  selectedAriaLabel: string;
  noMatchesMessage: string;
  placeholder?: string;
  unknownOptionLabel?: string;
  label?: string;
  required?: boolean;
  validationMessage?: string;
  disabled?: boolean;
  className?: string;
  /** 'tags' grows with the selection (forms/dialogs); 'summary' keeps one line for filter bars:
   * as many tags as fit plus a grey "+N", with every shown tag still dismissible. */
  variant?: 'tags' | 'summary';
};

const MultiSelectTagPicker = ({
  options,
  selectedOptions,
  onChange,
  inputAriaLabel,
  selectedAriaLabel,
  noMatchesMessage,
  placeholder,
  unknownOptionLabel = 'Unknown',
  label,
  required,
  validationMessage,
  disabled = false,
  className,
  variant = 'tags',
}: TProps) => {
  const classes = useStyles();
  const [query, setQuery] = useState('');
  const optionsByValue = useMemo(() => new Map(options.map((option) => [option.value, option])), [options]);
  const selectedOptionSet = useMemo(() => new Set(selectedOptions), [selectedOptions]);

  const onOptionSelect: TagPickerProps['onOptionSelect'] = (_, data) => {
    if (data.value === NO_MATCHES_OPTION_VALUE) return;
    const target = optionsByValue.get(data.value);
    if (target?.disabledReason && !selectedOptionSet.has(data.value)) return;
    onChange(data.selectedOptions);
    setQuery('');
  };

  const children = useTagPickerFilter({
    query,
    options: options.map((option) => option.value),
    noOptionsElement: <TagPickerOption value={NO_MATCHES_OPTION_VALUE}>{noMatchesMessage}</TagPickerOption>,
    renderOption: (value) => {
      const option = optionsByValue.get(value);
      const disabledReason = option?.disabledReason;
      return (
        <ControlledTooltip
          key={value}
          enable={Boolean(disabledReason)}
          content={disabledReason ?? ''}
          relationship="description"
          positioning="before"
          withArrow
        >
          <TagPickerOption
            value={value}
            text={option?.label ?? ''}
            aria-disabled={disabledReason ? true : undefined}
            className={disabledReason ? classes.disabledOption : undefined}
          >
            {option?.label ?? unknownOptionLabel}
          </TagPickerOption>
        </ControlledTooltip>
      );
    },
    filter: (value) => {
      const option = optionsByValue.get(value);
      if (!option || selectedOptionSet.has(value)) return false;
      return (option.searchText ?? option.label).toLowerCase().includes(query.toLowerCase());
    },
  });

  const renderTag = (value: string) => (
    <Tag key={value} shape="rounded" value={value} className="min-w-0">
      {optionsByValue.get(value)?.label ?? unknownOptionLabel}
    </Tag>
  );

  const control = (
    <>
      <TagPickerGroup
        aria-label={selectedAriaLabel}
        className={variant === 'summary' ? '!min-w-0 !flex-nowrap !gap-0 overflow-hidden' : undefined}
      >
        {variant === 'tags' ? (
          selectedOptions.map(renderTag)
        ) : (
          <MultiselectSummaryItems items={selectedOptions.map((value) => ({ value, content: renderTag(value) }))} />
        )}
      </TagPickerGroup>
      <TagPickerInput
        className={variant === 'summary' ? '!min-w-16 !w-16 pl-2' : undefined}
        aria-label={inputAriaLabel}
        placeholder={selectedOptions.length === 0 ? placeholder : undefined}
        value={query}
        onChange={(event) => setQuery(event.target.value)}
      />
    </>
  );

  const picker = (
    <TagPicker
      onOptionSelect={onOptionSelect}
      selectedOptions={selectedOptions}
      disabled={disabled}
      positioning={{ position: 'below', autoSize: 'height', pinned: true }}
    >
      <TagPickerControl className={variant === 'summary' ? '!flex-nowrap' : undefined}>
        {variant === 'summary' ? (
          // Measure the whole row, but let the group keep its natural width so the caret follows the tags.
          // Reserve 64px for the input (min-w-16), in addition to Overflow's default 10px padding.
          <Overflow minimumVisible={1} padding={64 + 10}>
            <div className="flex min-w-0 flex-1 items-center">{control}</div>
          </Overflow>
        ) : (
          control
        )}
      </TagPickerControl>
      <TagPickerList>{children}</TagPickerList>
    </TagPicker>
  );

  const content = label ? (
    <Field
      label={label}
      required={required}
      validationState={validationMessage ? 'error' : undefined}
      validationMessage={validationMessage}
    >
      {picker}
    </Field>
  ) : (
    picker
  );

  return className ? <div className={className}>{content}</div> : content;
};

export default MultiSelectTagPicker;
